<script setup lang="ts">
import type {
  DocumentDownloadFormat,
  DocumentItem,
} from '@/js/account/entities/document';
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import { AccountRouteName } from '@/js/account/app/router/route-names';
import ActionButton from '@/js/account/components/base/ActionButton.vue';
import BaseChip from '@/js/account/components/base/BaseChip.vue';
import DocumentReadTime from '@/js/account/components/document/DocumentReadTime.vue';
import DownloadFormatMenu from '@/js/account/components/document/DownloadFormatMenu.vue';
import MarkdownContent from '@/js/account/components/text-editor/MarkdownContent.vue';
import {
  getDocumentCoverUrl,
  getDocumentDownloadFormats,
  hasDocumentDownloads,
} from '@/js/account/entities/document';
import { classNames } from '@/js/lib/bem';

const props = withDefaults(
  defineProps<{
    document: DocumentItem
    downloading?: boolean
    deleting?: boolean
    showAdminActions?: boolean
  }>(),
  {
    downloading: false,
    deleting: false,
    showAdminActions: false,
  },
);

const emit = defineEmits<{
  download: [format: DocumentDownloadFormat]
  delete: []
}>();

const cn = classNames('document-card');

const documentRoute = computed(() => ({
  name: AccountRouteName.LibraryDocument,
  params: { id: String(props.document.id) },
}));

const coverUrl = computed(() => getDocumentCoverUrl(props.document));
const downloadFormats = computed(() =>
  getDocumentDownloadFormats(props.document),
);
const hasDownloadFormats = computed(() =>
  hasDocumentDownloads(props.document),
);

const editLabel = computed(() => `Edit ${props.document.title}`);
const deleteLabel = computed(() => `Delete ${props.document.title}`);

const hasBottom = computed(
  () =>
    props.document.tags.length > 0
    || hasDownloadFormats.value
    || props.showAdminActions,
);

const hasActions = computed(
  () => hasDownloadFormats.value || props.showAdminActions,
);

const editRoute = computed(() => ({
  name: AccountRouteName.AdminDocumentEdit,
  params: { id: String(props.document.id) },
}));

function onDownload(format: DocumentDownloadFormat): void {
  if (props.downloading || !hasDownloadFormats.value) {
    return;
  }

  emit('download', format);
}

function onDelete(): void {
  if (props.deleting) {
    return;
  }

  emit('delete');
}
</script>

<template>
  <article :class="cn()">
    <RouterLink :to="documentRoute" :class="cn('link')">
      <h3 :class="cn('title')">
        {{ document.title }}
      </h3>
      <DocumentReadTime
        :read-minutes="document.readMinutes"
        :class="cn('time')"
      />
      <img
        v-if="coverUrl"
        :class="cn('img')"
        :src="coverUrl"
        alt=""
        :width="498"
        :height="196"
      >
      <MarkdownContent
        :content="document.description"
        :class="cn('description')"
      />
    </RouterLink>

    <div v-if="hasBottom" :class="cn('bottom')">
      <div :class="cn('tags')">
        <BaseChip
          v-for="tag in document.tags"
          :key="tag.id"
          theme="soft"
          size="sm"
        >
          {{ tag.name }}
        </BaseChip>
      </div>
      <div v-if="hasActions" :class="cn('actions')">
        <DownloadFormatMenu
          v-if="hasDownloadFormats"
          :document-title="document.title"
          :formats="downloadFormats"
          :disabled="downloading"
          @download="onDownload"
        />
        <RouterLink
          v-if="showAdminActions"
          v-slot="{ navigate }"
          :to="editRoute"
          custom
        >
          <div :class="cn('action', { disabled: deleting })">
            <ActionButton
              icon="edit"
              :label="editLabel"
              :disabled="deleting"
              @click="navigate"
            />
          </div>
        </RouterLink>
        <div
          v-if="showAdminActions"
          :class="cn('action', { disabled: deleting })"
        >
          <ActionButton
            icon="trash"
            :label="deleteLabel"
            :disabled="deleting"
            @click="onDelete"
          />
        </div>
      </div>
    </div>
  </article>
</template>

<style lang="scss" scoped>
.document-card {
  border-radius: 4px;
  background-color: var(--color-white);
  padding: 16px;
  box-shadow: var(--shadow);
  color: inherit;

  &__link {
    display: block;
    color: inherit;
    text-decoration: none;
  }

  &__title {
    font-size: 16px;
    font-weight: 500;
    line-height: 1.2;
    color: var(--color-text-primary);
    margin: 0 0 16px;
  }

  &__time {
    margin: 0 0 24px;
  }

  &__img {
    max-width: 498px;
    max-height: 196px;
    width: 100%;
    height: auto;
    object-fit: cover;
    border-radius: 4px;
    margin: 0 0 24px;
  }

  &__description {
    margin: 0 0 16px;
  }

  &__bottom {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 16px;
  }

  &__tags {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
    color: inherit;
    text-decoration: none;
  }

  &__actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
    margin-left: auto;
  }

  &__action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 4px;
    transition: background-color 0.2s ease;

    &:hover:not(&_disabled) {
      background-color: var(--color-background-primary);
    }

    &_disabled {
      opacity: 0.6;
    }
  }

  @media (max-width: 768px) {
    &__title {
      margin: 0 0 8px;
    }

    &__img {
      max-width: 100%;
    }
  }
}
</style>
